import { FC, Fragment, useCallback, useEffect, useState } from "react";
import { Popover, PopoverProps, Stack, Typography } from "@mui/material";
import { useDebounce } from "rooks";
import { FilterItemNodeWithId } from "@/components/pages/common/filters-tree/utils/types/common";
import { FilterValueWithBatchInputTextArea } from "@/components/pages/common/filters-tree/filters-selection/components/provider-dynamic-filters/filter-item/components/filter-value/components/filter-value-with-batch-input/components/FilterValueWithBatchInputTextArea";
import { FilterValueWithBatchInputSearchingPhase } from "@/components/pages/common/filters-tree/filters-selection/components/provider-dynamic-filters/filter-item/components/filter-value/components/filter-value-with-batch-input/components/FilterValueWithBatchInputSearchingPhase";
import { FilterValueWithBatchInputActions } from "@/components/pages/common/filters-tree/filters-selection/components/provider-dynamic-filters/filter-item/components/filter-value/components/filter-value-with-batch-input/components/FilterValueWithBatchInputActions";
import { FilterValueWithBatchInputPopoverFinalPhase } from "@/components/pages/common/filters-tree/filters-selection/components/provider-dynamic-filters/filter-item/components/filter-value/components/filter-value-with-batch-input/components/FilterValueWithBatchInputPopoverFinalPhase";
import {
  KeyValuePair,
  usePostUsersMeOrganisationsCurrentByProviderAndValidationFilterTypeValidValuesMutation as useFilterValidValuesMutation,
} from "@/services/cloudchipr.api";
import {
  generateValidatorPayload,
  getBatchValuesFromText,
} from "@/components/pages/common/filters-tree/filters-selection/components/provider-dynamic-filters/filter-item/components/filter-value/components/filter-value-with-batch-input/utils/helpers/helpers";

interface FilterValueWithBatchInputPopoverProps {
  open: boolean;
  onClose(): void;
  valueHasKey: boolean;
  anchor: HTMLElement | null;
  filter: FilterItemNodeWithId;
  onChange(value: FilterItemNodeWithId["value"]): void;
}

export const FilterValueWithBatchInputPopover: FC<
  FilterValueWithBatchInputPopoverProps
> = ({ filter, onChange, open, anchor, onClose, valueHasKey }) => {
  const [valuesValidator] = useFilterValidValuesMutation();

  const filterValueKey = valueHasKey
    ? (filter.value as KeyValuePair)?.key
    : undefined;

  const [text, setText] = useState("");
  const [batchValues, setBatchValues] = useState<string[]>([]);
  const [validValues, setValidValues] = useState<string[]>([]);
  const [phase, setPhase] = useState<"initialInput" | "searching" | "final">(
    "initialInput",
  );
  const enteredValuesCount = batchValues.length;

  const setBatchValuesDebounced = useDebounce(setBatchValues, 500);

  const valuesEnterHandler = useCallback(async () => {
    setPhase("searching");
    const response = await valuesValidator(
      generateValidatorPayload(filter, batchValues, filterValueKey),
    );

    if (!("data" in response)) {
      return;
    }

    if (!response?.data) {
      return;
    }

    setValidValues((values) =>
      Array.from(new Set([...(response?.data?.valid ?? []), ...values])),
    );
    setText(response?.data?.invalid?.join(", "));
    setBatchValues(response?.data?.invalid ?? []);

    setPhase("final");
  }, [valuesValidator, batchValues, filter, filterValueKey]);

  const closeHandler = useCallback(() => {
    onClose();
    setText("");
    setBatchValues([]);
    setValidValues([]);
    setPhase("initialInput");
  }, [onClose]);

  const valuesApplyHandler = useCallback(() => {
    if (valueHasKey && filterValueKey) {
      onChange({ key: filterValueKey, value: validValues });
    } else {
      onChange(validValues);
    }

    closeHandler();
  }, [validValues, valueHasKey, filterValueKey, onChange, closeHandler]);

  useEffect(() => {
    const values = getBatchValuesFromText(text);
    setBatchValuesDebounced(values);
  }, [text, setBatchValuesDebounced]);

  const filterValue = valueHasKey
    ? (filter.value as KeyValuePair)?.value
    : filter.value;

  useEffect(() => {
    if (open && Array.isArray(filterValue) && !!filterValue.length) {
      setText(filterValue.join(", "));
    }
  }, [open, filterValue]);

  return (
    <Popover
      open={open}
      anchorEl={anchor}
      onClose={closeHandler}
      anchorOrigin={anchorOrigin}
      transformOrigin={transformOrigin}
    >
      <Stack width={600} p={1.5} spacing={1.5}>
        {phase === "initialInput" && (
          <Fragment>
            <Typography>
              Enter or paste a list of values, separated by commas or new lines.
            </Typography>

            <Stack>
              <FilterValueWithBatchInputTextArea
                value={text}
                onChange={setText}
              />

              {!!enteredValuesCount && (
                <Typography variant="caption" pl={1}>
                  You have entered {enteredValuesCount} value
                  {enteredValuesCount > 1 ? "s" : ""}.
                </Typography>
              )}
            </Stack>

            <FilterValueWithBatchInputActions
              variant="contained"
              submitLabel="Search"
              onClose={closeHandler}
              disabled={!enteredValuesCount}
              onSubmit={valuesEnterHandler}
            />
          </Fragment>
        )}

        {phase === "searching" && (
          <FilterValueWithBatchInputSearchingPhase count={enteredValuesCount} />
        )}

        {phase === "final" && (
          <FilterValueWithBatchInputPopoverFinalPhase
            text={text}
            setText={setText}
            onClose={closeHandler}
            batchValues={batchValues}
            validValues={validValues}
            onValueApply={valuesApplyHandler}
            onValuesEnter={valuesEnterHandler}
            enteredValuesCount={enteredValuesCount}
          />
        )}
      </Stack>
    </Popover>
  );
};

const anchorOrigin: PopoverProps["anchorOrigin"] = {
  vertical: "bottom",
  horizontal: "center",
};
const transformOrigin: PopoverProps["transformOrigin"] = {
  vertical: "top",
  horizontal: "center",
};
